import clsx from "clsx";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import type { IImageViewerProps } from ".";

import { Icon } from "@components/atoms/icons";
import { IconButton } from "@components/molecules/iconButton";

const ZOOM_RATE = 0.1;

export function ImageViewer({ src, title }: IImageViewerProps) {
  const { t } = useTranslation("actions");

  const containerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLImageElement>(null);
  const [imgScale, setImgScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (isDragging && containerRef.current) {
      const deltaX = event.clientX - dragStart.x;
      const deltaY = event.clientY - dragStart.y;

      containerRef.current.scrollLeft -= deltaX;
      containerRef.current.scrollTop -= deltaY;

      setDragStart({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseUp = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setIsDragging(false);
  };

  return (
    <div className={clsx(classes.imgViewer)}>
      <div className={clsx(classes.container)}>
        <div
          className={clsx(
            classes.imgContainer,
            isDragging && classes.dragCursor,
          )}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <img
            ref={ref}
            style={{
              width: `${imgScale * 100}%`,
            }}
            src={src}
            alt={title}
            className={clsx(classes.imgContainer__image)}
            // onDoubleClick={handleDoubleClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          />
        </div>
      </div>
      <div className={clsx(classes.imgViewer__button)}>
        <IconButton
          icon={<Icon name={"MagniferZoomIn"} type={"linear"} />}
          label={t("zoom-in")}
          onClick={() => {
            setImgScale((prevState) => prevState + ZOOM_RATE);
          }}
          disabled={imgScale > 50}
        />
        <IconButton
          icon={<Icon name={"MagniferZoomOut"} type={"linear"} />}
          label={t("zoom-out")}
          onClick={() => {
            setImgScale((prevState) => prevState - ZOOM_RATE);
          }}
          disabled={imgScale <= 1}
        />
      </div>
    </div>
  );
}
